import { AppointmentFileView } from "@/types/appointment";
import { apiUrl } from "@/utils/url";
import { FILE_TRANSFER_TIMEOUT_MS, HttpClient } from "../HttpClient";
import { getErrorMessage } from "../errors";
import {
  AppointmentFileMetadata,
  AppointmentFilesPageResponse,
  AppointmentFilesPageable,
  IAppointmentFilesService,
} from "./IAppointmentFilesService";

export class AppointmentFilesService implements IAppointmentFilesService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 30000,
    });
  }

  async getAppointmentFileById(
    appointmentFileId: number,
    includePresignedURL?: boolean,
  ): Promise<AppointmentFileView> {
    try {
      const params = new URLSearchParams();
      if (typeof includePresignedURL === "boolean") {
        params.append("includePresignedURL", String(includePresignedURL));
      }

      const suffix = params.toString() ? `?${params.toString()}` : "";
      return await this.httpClient.get<AppointmentFileView>(
        `/appointmentFiles/${appointmentFileId}${suffix}`,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os ficheiros do atendimento. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getAppointmentFilesByAppointmentId(
    appointmentId: number,
    options?: {
      pageable?: AppointmentFilesPageable;
      fileTypeId?: number;
      includePresignedURL?: boolean;
    },
  ): Promise<AppointmentFilesPageResponse> {
    try {
      const pageable = options?.pageable ?? { page: 0, size: 100 };
      const params = new URLSearchParams({
        page: String(pageable.page),
        size: String(pageable.size),
      });
      if (pageable.sort) {
        params.append("sort", pageable.sort);
      }
      if (typeof options?.fileTypeId === "number") {
        params.append("fileTypeId", String(options.fileTypeId));
      }
      if (typeof options?.includePresignedURL === "boolean") {
        params.append(
          "includePresignedURL",
          String(options.includePresignedURL),
        );
      }

      const data = await this.httpClient.get<unknown>(
        `/appointmentFiles/appointment/${appointmentId}?${params.toString()}`,
      );

      return normalizeAppointmentFilesResponse(data);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os ficheiros do atendimento. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async uploadAppointmentFile(
    metadata: AppointmentFileMetadata,
    file: File | Blob,
  ): Promise<number> {
    try {
      const formData = new FormData();
      formData.append(
        "metadata",
        new Blob([JSON.stringify(metadata)], { type: "application/json" }),
      );
      formData.append("file", file);

      return await this.httpClient.post<number>(
        "/appointmentFiles",
        formData,
        {
          timeout: FILE_TRANSFER_TIMEOUT_MS,
          headers: {
            Accept: "application/json",
          },
        },
        true,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar o ficheiro do atendimento. Confirme o tipo e tamanho do ficheiro.",
      );
      throw new Error(errorMessage);
    }
  }

  async deleteAppointmentFile(appointmentFileId: number): Promise<void> {
    try {
      await this.httpClient.delete(`/appointmentFiles/${appointmentFileId}`);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível apagar o ficheiro. Atualize a lista de ficheiros e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }
}

const normalizeAppointmentFilesResponse = (
  raw: unknown,
): AppointmentFilesPageResponse => {
  if (Array.isArray(raw)) {
    const content = raw as AppointmentFileView[];
    return {
      content,
      page: {
        size: content.length,
        number: 0,
        totalElements: content.length,
        totalPages: content.length > 0 ? 1 : 0,
      },
    };
  }

  const content = Array.isArray((raw as any)?.content)
    ? ((raw as any).content as AppointmentFileView[])
    : [];

  return {
    content,
    page: {
      size: Number((raw as any)?.page?.size ?? content.length ?? 0),
      number: Number((raw as any)?.page?.number ?? 0),
      totalElements: Number(
        (raw as any)?.page?.totalElements ?? content.length ?? 0,
      ),
      totalPages: Number(
        (raw as any)?.page?.totalPages ?? (content.length > 0 ? 1 : 0),
      ),
    },
  };
};
